import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react'
import ProductCard from '../ui/ProductCard'
import { useTranslation } from 'react-i18next'

const ProductSliderSection = ({
    title,
    subtitle,
    products = [],
    viewAllHref,
    bgColor = "bg-white/40"
}) => {
    const { t } = useTranslation();
    const sliderRef = useRef(null);

    const scrollSlider = (direction) => {
        if (!sliderRef.current) return;
        const scrollAmount = 300; // approx card width + gap
        sliderRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    };

    if (!products || products.length === 0) return null;

    return (
        <section className={`py-12 md:py-16 ${bgColor} border-y border-fecos-border/30`}>
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 space-y-8">

                {/* Header */}
                <div className="flex items-end justify-between">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-fecos-text tracking-tight">
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="text-fecos-gray mt-1 md:mt-2 text-base md:text-lg">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    {/* Desktop/Tablet Controls */}
                    <div className="hidden sm:flex items-center gap-3">
                        {viewAllHref && (
                            <Link
                                to={viewAllHref}
                                className="mr-4 flex items-center gap-2 text-sm font-semibold text-fecos-accent hover:text-fecos-accent/80 transition-colors group"
                            >
                                {t('common.viewAll')}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        )}
                        <button
                            onClick={() => scrollSlider('left')}
                            className="p-2.5 rounded-full bg-white border border-fecos-border shadow-sm hover:shadow-md hover:bg-fecos-bg transition-all active:scale-95 text-fecos-text"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => scrollSlider('right')}
                            className="p-2.5 rounded-full bg-white border border-fecos-border shadow-sm hover:shadow-md hover:bg-fecos-bg transition-all active:scale-95 text-fecos-text"
                            aria-label="Next"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Slider Container */}
                <div className="relative group/slider">
                    {/* Mobile: 1.2 cards visible, Tablet: 3, Desktop: 4 */}
                    <div
                        ref={sliderRef}
                        className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-8 pt-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {products.map((product) => (
                            <div
                                key={product.id}
                                className="min-w-[260px] w-[75%] sm:w-[calc(50%-12px)] md:w-[calc(33.33%-16px)] lg:w-[calc(25%-18px)] snap-start flex-shrink-0"
                            >
                                <ProductCard product={product} />
                            </div>
                        ))}

                        {/* Spacer for right padding on mobile scroll */}
                        <div className="w-[1px] flex-shrink-0 h-1" />
                    </div>

                    {/* Mobile 'View All' Bottom Link */}
                    {viewAllHref && (
                        <div className="sm:hidden flex justify-center mt-2">
                            <Link
                                to={viewAllHref}
                                className="flex items-center gap-2 text-sm font-semibold text-fecos-accent"
                            >
                                {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    )}
                </div>

            </div>
        </section>
    );
};

export default ProductSliderSection;
